<template>
  <div class="dashboard">
    <section class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">Command Center</h1>
        <p class="hero-subtitle">Real-time monitoring and control interface for your smart home ecosystem.</p>
      </div>
      <div class="hero-stat">
        <span class="stat-value">{{ counter }}</span>
        <span class="stat-label">Live Ticks</span>
      </div>
    </section>

    <section class="metrics-grid">
      <div class="metric-card">
        <div class="metric-icon status-online">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a10 10 0 1 0 10 10H12V2z"/>
            <path d="M12 12L2.5 8.5"/>
          </svg>
        </div>
        <div class="metric-body">
          <span class="metric-label">System Status</span>
          <span class="metric-value online">Online</span>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon status-warning">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
          </svg>
        </div>
        <div class="metric-body">
          <span class="metric-label">Active Sensors</span>
          <span class="metric-value">{{ sensorCount }}</span>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon status-info">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <div class="metric-body">
          <span class="metric-label">Messages</span>
          <span class="metric-value">{{ counter }}</span>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon status-neutral">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
        </div>
        <div class="metric-body">
          <span class="metric-label">Environment</span>
          <span class="metric-value">Stable</span>
        </div>
      </div>
    </section>

    <section class="panel-grid">
      <div class="panel-card wide">
        <div class="panel-header">
          <h3>System Overview</h3>
          <span class="panel-badge">Live</span>
        </div>
        <div class="panel-body">
          <p>Welcome to the Smart Home Control Center. Use the sidebar to navigate between the dashboard, task queue, and system settings.</p>
          <div class="feature-list">
            <div class="feature-item">
              <span class="feature-dot"></span>
              <span>MQTT device control</span>
            </div>
            <div class="feature-item">
              <span class="feature-dot"></span>
              <span>Real-time sensor monitoring</span>
            </div>
            <div class="feature-item">
              <span class="feature-dot"></span>
              <span>WebSocket event stream</span>
            </div>
          </div>
        </div>
      </div>

      <div class="panel-card">
        <div class="panel-header">
          <h3>Quick Actions</h3>
        </div>
        <div class="panel-body actions">
          <button class="action-btn primary" @click="emitPing">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            <span>Send Ping</span>
          </button>
          <button class="action-btn" @click="$router.push('/settings')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 20h9"/>
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
            </svg>
            <span>Configure</span>
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'DashboardView',
  props: {
    msg: String
  },
  data() {
    return {
      sensorCount: 0,
      counter: 0
    }
  },
  sockets: {
    hello(data) {
      this.counter = data
    }
  },
  methods: {
    emitPing() {
      this.$socket.emit('hi', { message: 'ping', time: Date.now() })
    },
    loadSensors() {
      this.axios.get(process.env.VUE_APP_BACK_URL + '/sensor')
          .then(res => {
            const data = res.data?.data || res.data
            this.sensorCount = Array.isArray(data) ? data.length : 0
          })
          .catch(() => {
            this.sensorCount = 0
          })
    }
  },
  mounted() {
    this.loadSensors()
  }
}
</script>

<style scoped>
.dashboard {
  max-width: 1200px;
}

.hero-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
  padding: 28px 32px;
  border-radius: var(--radius);
  background:
      linear-gradient(135deg, rgba(0, 212, 255, 0.08), rgba(0, 102, 255, 0.04)),
      var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
}

.hero-title {
  font-family: var(--font-display);
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px;
  letter-spacing: 0.02em;
}

.hero-subtitle {
  color: var(--text-secondary);
  margin: 0;
  max-width: 480px;
  line-height: 1.5;
}

.hero-stat {
  text-align: right;
}

.stat-value {
  display: block;
  font-family: var(--font-mono);
  font-size: 2.4rem;
  font-weight: 700;
  color: var(--accent);
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-muted);
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 28px;
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border-radius: var(--radius);
  background: var(--surface);
  border: 1px solid var(--border);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}

.metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.metric-icon svg {
  width: 24px;
  height: 24px;
}

.status-online {
  color: var(--success);
  background: rgba(0, 230, 118, 0.1);
}

.status-warning {
  color: var(--warning);
  background: rgba(255, 145, 0, 0.1);
}

.status-info {
  color: var(--accent);
  background: var(--accent-dim);
}

.status-neutral {
  color: var(--text-secondary);
  background: rgba(148, 163, 184, 0.1);
}

.metric-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}

.metric-value {
  font-family: var(--font-mono);
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-primary);
}

.metric-value.online {
  color: var(--success);
}

.panel-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
}

.panel-card {
  border-radius: var(--radius);
  background: var(--surface);
  border: 1px solid var(--border);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border);
}

.panel-header h3 {
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0;
}

.panel-badge {
  padding: 4px 10px;
  border-radius: 20px;
  background: rgba(0, 230, 118, 0.1);
  color: var(--success);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.panel-body {
  padding: 22px;
  color: var(--text-secondary);
  line-height: 1.6;
}

.panel-body p {
  margin: 0 0 18px;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
}

.feature-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 8px var(--accent);
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 18px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: var(--surface-hover);
  border-color: var(--border-strong);
}

.action-btn.primary {
  background: linear-gradient(135deg, var(--accent), #0066ff);
  border-color: transparent;
  color: #fff;
}

.action-btn.primary:hover {
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
}

.action-btn svg {
  width: 18px;
  height: 18px;
}

@media (max-width: 980px) {
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .panel-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .hero-section {
    flex-direction: column;
    align-items: flex-start;
  }

  .hero-stat {
    text-align: left;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style>
